import CalculateIcon from "@mui/icons-material/Calculate";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DescriptionIcon from "@mui/icons-material/Description";
import EventNoteIcon from "@mui/icons-material/EventNote";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import HandshakeIcon from "@mui/icons-material/Handshake";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SearchIcon from "@mui/icons-material/Search";
import SpeedIcon from "@mui/icons-material/Speed";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SmsIcon from "@mui/icons-material/Sms";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import DefaultApiService from "../../../../infrastructure/api/default";
import RequestAssignmentApi from "../../../../infrastructure/api/requestAssignment";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import TabPanel from "../../../components/shared/tab-panel";
import {
  normalizeGrowthAnalytics,
  EMPTY_GROWTH_ANALYTICS,
} from "../components/growthAnalyticsConstants";

const GrowthConversionAnalyticsPanel = lazy(() =>
  import("../components/GrowthConversionAnalyticsPanel")
);

const CARD_SX = {
  borderRadius: "16px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
};

const SECTION_SPACING = 2;
const PAGE_BG = "#F6F8FB";

const PATHS = {
  checkNew: "/SLN/SolarentOperator/CheckNewRequest",
  inProcess: "/SLN/SolarentOperator/InProcessRequest",
  projectWorkspace: "/SLN/SolarentOperator/ProjectWorkspace",
  operationalReport: "/SLN/SolarentOperator/OperationalProcessesReport",
  mehrsanWorkQueue: "/SLN/SolarentOperator/MehrsanWorkQueue",
  contractorReferralQueue: "/SLN/SolarentOperator/ContractorReferralWorkQueue",
  supervisionWorkQueue: "/SLN/SolarentOperator/SupervisionWorkQueue",
  fulfillmentWorkQueue: "/SLN/SolarentOperator/FulfillmentWorkQueue",
  barghPayQueue: "/SLN/SolarentOperator/BarghPay",
  iriaPayQueue: "/SLN/SolarentOperator/IriaPay",
  loanDisbursements: "/SLN/SolarentOperator/LoanDisbursements",
  loanSimulator: "/SLN/SolarentOperator/LoanSimulator",
  applicantSms: "/SLN/SolarentOperator/ApplicantSms",
  applicantContracts: "/SLN/SolarentOperator/ApplicantContracts",
  applicantContractFeeSettings:
    "/SLN/SolarentOperator/ApplicantContractFeeSettings",
  applicantRegistration: "/SLN/SolarentOperator/ApplicantRegistration",
  initialReviewFollowUp: "/SLN/SolarentOperator/InitialReviewFollowUp",
};

const formatPersianNumber = (value) =>
  Number(value || 0).toLocaleString("fa-IR");

const formatTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleTimeString("fa-IR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const formatResponseTime = (hours) => {
  if (hours == null || Number.isNaN(hours)) return "—";
  if (hours < 24) return `${formatPersianNumber(Math.round(hours))} ساعت`;
  const days = Math.round(hours / 24);
  return `${formatPersianNumber(days)} روز`;
};

const getUrgencyColor = (waitingHours, waitingDays) => {
  const hours = waitingHours ?? (waitingDays != null ? waitingDays * 24 : 0);
  if (hours < 24) return "#2e7d32";
  if (hours <= 72) return "#ed6c02";
  return "#d32f2f";
};

const getWaitingDurationLabel = (item) => {
  if (item.waitingHours != null && item.waitingHours < 24) {
    if (item.waitingHours < 1) return "کمتر از ۱ ساعت";
    return `${formatPersianNumber(item.waitingHours)} ساعت`;
  }
  return `${formatPersianNumber(item.waitingDays || 0)} روز`;
};

const EMPTY_WORKSPACE = {
  operatorFullName: "",
  showTeamPerformance: false,
  claimSettings: {
    assignmentEnabled: true,
    canClaimRequests: true,
    openCasesCount: 0,
    maxOpenCasesPerOperator: 20,
    isAtOpenCasesLimit: false,
    limitMessage: null,
  },
  newRequestQueue: [],
  myTasks: {
    initialReviewsPending: 0,
    siteVisitsPending: 0,
    loanFollowUps: 0,
    mehrsunFollowUps: 0,
    contractsAwaitingApproval: 0,
  },
  urgentCases: [],
  workloadSummary: {
    myOpenCases: 0,
    completedThisMonth: 0,
    averageResponseTimeHours: null,
    overdueCases: 0,
  },
  teamPerformance: [],
  workflowPipeline: [],
  todaysActivities: [],
  personalPerformance: {
    casesReviewedThisMonth: 0,
    averageResponseTimeHours: null,
    completedProjects: 0,
    activeProjects: 0,
  },
  growthAnalytics: null,
};

const MY_CASE_ITEMS = [
  {
    key: "initialReviewsPending",
    title: "بررسی اولیه",
    color: "#1976d2",
    path: `${PATHS.checkNew}?assignmentFilter=mine`,
  },
  {
    key: "siteVisitsPending",
    title: "بازدید",
    color: "#ed6c02",
    path: PATHS.inProcess,
  },
  {
    key: "loanFollowUps",
    title: "پیگیری وام",
    color: "#2e7d32",
    path: PATHS.operationalReport,
  },
  {
    key: "mehrsunFollowUps",
    title: "پیگیری مهرسان",
    color: "#9c27b0",
    path: PATHS.mehrsanWorkQueue,
  },
  {
    key: "contractsAwaitingApproval",
    title: "قراردادهای در انتظار",
    color: "#d32f2f",
    path: PATHS.inProcess,
  },
];

const QUICK_ACTIONS = [
  { title: "جستجوی پرونده", icon: <SearchIcon fontSize="small" />, color: "#0288d1", path: PATHS.projectWorkspace },
  { title: "پرونده‌های من", icon: <FolderOpenOutlinedIcon fontSize="small" />, color: "#1565c0", path: PATHS.inProcess },
  { title: "درخواست‌های جدید", icon: <InboxOutlinedIcon fontSize="small" />, color: "#1976d2", path: PATHS.checkNew },
  { title: "شبیه‌ساز وام", icon: <CalculateIcon fontSize="small" />, color: "#00838f", path: PATHS.loanSimulator },
  { title: "پیامک به متقاضی", icon: <SmsIcon fontSize="small" />, color: "#6a1b9a", path: PATHS.applicantSms },
  { title: "پیگیری وام", icon: <MonetizationOnIcon fontSize="small" />, color: "#2e7d32", path: PATHS.operationalReport },
  { title: "پیگیری مهرسان", icon: <AssignmentIcon fontSize="small" />, color: "#9c27b0", path: PATHS.mehrsanWorkQueue },
  { title: "ارجاع و قیمت پیمانکار", icon: <HandshakeIcon fontSize="small" />, color: "#1565c0", path: PATHS.contractorReferralQueue },
  { title: "کارتابل نظارت", icon: <VisibilityIcon fontSize="small" />, color: "#455a64", path: PATHS.supervisionWorkQueue },
  { title: "تامین، ارسال و اجرا", icon: <AssignmentIcon fontSize="small" />, color: "#ef6c00", path: PATHS.fulfillmentWorkQueue },
  { title: "میز کار برق‌پی", icon: <MonetizationOnIcon fontSize="small" />, color: "#00796b", path: PATHS.barghPayQueue },
  { title: "میز کار ایریاپی", icon: <MonetizationOnIcon fontSize="small" />, color: "#5d4037", path: PATHS.iriaPayQueue },
  { title: "واریز وام لندتک‌ها", icon: <MonetizationOnIcon fontSize="small" />, color: "#2e7d32", path: PATHS.loanDisbursements },
  { title: "گزارش عملیات", icon: <AssessmentIcon fontSize="small" />, color: "#ed6c02", path: PATHS.operationalReport },
  { title: "قراردادهای متقاضیان", icon: <DescriptionIcon fontSize="small" />, color: "#5c6bc0", path: PATHS.applicantContracts },
  { title: "تنظیمات هزینه کنتور", icon: <MonetizationOnIcon fontSize="small" />, color: "#6d4c41", path: PATHS.applicantContractFeeSettings },
  { title: "ثبت‌نام متقاضی", icon: <PersonAddIcon fontSize="small" />, color: "#43a047", path: PATHS.applicantRegistration },
  { title: "پیگیری بررسی اولیه", icon: <PendingActionsIcon fontSize="small" />, color: "#ef6c00", path: PATHS.initialReviewFollowUp },
];

const PIPELINE_COLORS = {
  InitialReview: "#1976d2",
  Visit: "#ed6c02",
  Design: "#0288d1",
  Loan: "#2e7d32",
  Mehrsun: "#9c27b0",
  PreExecution: "#f57c00",
  Execution: "#1565c0",
  Completed: "#388e3c",
};

const WORKLOAD_KPIS = [
  { key: "myOpenCases", label: "پرونده فعال" },
  { key: "completedThisMonth", label: "تکمیل شده ماه جاری" },
  { key: "overdueCases", label: "پرونده معوق" },
  { key: "averageResponseTimeHours", label: "میانگین زمان پاسخ", format: formatResponseTime },
];

const PERFORMANCE_KPIS = [
  { key: "myOpenCases", label: "پرونده‌های فعال" },
  { key: "completedThisMonth", label: "پرونده‌های تکمیل شده" },
  { key: "averageResponseTimeHours", label: "میانگین زمان پاسخ", format: formatResponseTime },
  { key: "overdueCases", label: "پرونده‌های معوق" },
];

const SectionHeader = ({ title, subtitle, action }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    sx={{ mb: 1.5 }}
  >
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}
    </Box>
    {action || null}
  </Stack>
);

const CompactEmptyState = ({ message, icon: Icon = InboxOutlinedIcon }) => (
  <Box
    sx={{
      py: 2.5,
      px: 2,
      textAlign: "center",
      color: "text.secondary",
      bgcolor: "#fafbfc",
      borderRadius: "12px",
      border: "1px dashed #e8eaed",
    }}
  >
    <Icon sx={{ fontSize: 28, color: "#c5c9d0", mb: 0.75 }} />
    <Typography variant="body2" fontSize={13}>
      {message}
    </Typography>
  </Box>
);

const CompactKpiRow = ({ items, data }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, 1fr)",
        sm: "repeat(4, 1fr)",
      },
      gap: 1.5,
    }}
  >
    {items.map((item) => {
      const raw = data[item.key];
      const value = item.format ? item.format(raw) : formatPersianNumber(raw);
      return (
        <Box
          key={item.key}
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: "12px",
            bgcolor: "#fafbfc",
            border: "1px solid #eee",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3}>
            {item.label}
          </Typography>
          <Typography fontWeight={800} fontSize={18} sx={{ mt: 0.25 }}>
            {value}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const normalizeWorkspace = (data) => ({
  ...EMPTY_WORKSPACE,
  ...data,
  claimSettings: { ...EMPTY_WORKSPACE.claimSettings, ...(data.claimSettings || {}) },
  myTasks: { ...EMPTY_WORKSPACE.myTasks, ...(data.myTasks || {}) },
  workloadSummary: { ...EMPTY_WORKSPACE.workloadSummary, ...(data.workloadSummary || {}) },
  personalPerformance: {
    ...EMPTY_WORKSPACE.personalPerformance,
    ...(data.personalPerformance || {}),
  },
  newRequestQueue: data.newRequestQueue || [],
  urgentCases: data.urgentCases || [],
  teamPerformance: data.teamPerformance || [],
  workflowPipeline: data.workflowPipeline || [],
  todaysActivities: data.todaysActivities || [],
  growthAnalytics: normalizeGrowthAnalytics(data.growthAnalytics),
});

const SolarentOperatorPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [workspace, setWorkspace] = useState(EMPTY_WORKSPACE);
  const [claimingId, setClaimingId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [growthAnalytics, setGrowthAnalytics] = useState(EMPTY_GROWTH_ANALYTICS);
  const [growthLoading, setGrowthLoading] = useState(false);
  const growthFetchStarted = useRef(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadWorkspace = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError("");

      const res = await DefaultApiService.getApi(
        "SLN/SolarentOperator/OperatorDashboard/GetWorkspace",
        {},
        false
      );

      if (!res) return;

      if (res.isSuccess && res.data) {
        setWorkspace(normalizeWorkspace(res.data));
        setError("");
      } else {
        setError(
          res.message ||
            (res.statusCode === 6
              ? "لطفاً دوباره وارد حساب کاربری شوید."
              : "خطا در دریافت داشبورد عملیاتی")
        );
      }
    } catch (err) {
      if (err?.isAuthError) return;
      setError(err?.message || "خطا در ارتباط با سرور");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const loadGrowthAnalytics = useCallback(async () => {
    if (growthFetchStarted.current) return;
    growthFetchStarted.current = true;

    try {
      setGrowthLoading(true);
      const res = await DefaultApiService.getApi(
        "SLN/SolarentOperator/OperatorDashboard/GetGrowthAnalytics",
        { periodDays: 30, aggregation: 1 },
        false
      );

      if (res?.isSuccess && res.data) {
        setGrowthAnalytics(normalizeGrowthAnalytics(res.data));
      } else {
        growthFetchStarted.current = false;
      }
    } catch (err) {
      growthFetchStarted.current = false;
      if (!err?.isAuthError) {
        console.warn("growth analytics load failed", err);
      }
    } finally {
      setGrowthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 3) {
      loadGrowthAnalytics();
    }
  }, [tabValue, loadGrowthAnalytics]);

  const handleClaim = async (requestId) => {
    const claimSettings = workspace.claimSettings || {};
    if (!claimSettings.canClaimRequests || claimingId) return;

    try {
      setClaimingId(requestId);
      const res = await RequestAssignmentApi.claim("SolarentOperator", requestId);
      if (res?.isSuccess) {
        setSnackbar({
          open: true,
          message: res.message || "پرونده با موفقیت دریافت شد.",
          severity: "success",
        });
        await loadWorkspace(true);
      } else {
        setSnackbar({
          open: true,
          message: res?.message || "خطا در دریافت پرونده",
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "خطا در ارتباط با سرور",
        severity: "error",
      });
    } finally {
      setClaimingId(null);
    }
  };

  const totalMyCases = useMemo(() => {
    const t = workspace.myTasks;
    return (
      (t.initialReviewsPending || 0) +
      (t.siteVisitsPending || 0) +
      (t.loanFollowUps || 0) +
      (t.mehrsunFollowUps || 0) +
      (t.contractsAwaitingApproval || 0)
    );
  }, [workspace.myTasks]);

  const maxPipelineCount = useMemo(() => {
    const counts = workspace.workflowPipeline.map((s) => s.count || 0);
    return Math.max(...counts, 1);
  }, [workspace.workflowPipeline]);

  const hasPipelineData = useMemo(
    () => workspace.workflowPipeline.some((s) => s.count > 0),
    [workspace.workflowPipeline]
  );

  const hasPerformanceData = useMemo(() => {
    const w = workspace.workloadSummary;
    return (
      (w.myOpenCases || 0) > 0 ||
      (w.completedThisMonth || 0) > 0 ||
      (w.overdueCases || 0) > 0 ||
      (w.averageResponseTimeHours != null && w.averageResponseTimeHours > 0)
    );
  }, [workspace.workloadSummary]);

  const claimSettings = workspace.claimSettings || EMPTY_WORKSPACE.claimSettings;
  const workload = workspace.workloadSummary || EMPTY_WORKSPACE.workloadSummary;
  const newRequestQueue = workspace.newRequestQueue.slice(0, 5);

  return (
    <Box dir="rtl" sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 }, background: PAGE_BG }}>
      {/* Header + Quick Actions */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <BreadcrumbsComponent />
        {loading ? (
          <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
        ) : null}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={1.5}
          sx={{ mt: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              فضای کاری کارشناس
            </Typography>
            <Typography variant="body2" color="text.secondary" fontSize={13}>
              {workspace.operatorFullName
                ? `سلام ${workspace.operatorFullName}`
                : "مدیریت پرونده‌ها و اقدامات عملیاتی"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {QUICK_ACTIONS.map((action) => (
              <Tooltip key={action.title} title={action.title} arrow>
                <IconButton
                  size="small"
                  onClick={() => navigate(action.path)}
                  sx={{
                    bgcolor: `${action.color}12`,
                    color: action.color,
                    border: "1px solid #e8eaed",
                    "&:hover": { bgcolor: `${action.color}22` },
                  }}
                >
                  {action.icon}
                </IconButton>
              </Tooltip>
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: "none", sm: "block" } }} />
            <Chip
              size="small"
              icon={<WorkOutlineIcon />}
              label={`${formatPersianNumber(claimSettings.openCasesCount)}/${formatPersianNumber(claimSettings.maxOpenCasesPerOperator)}`}
              color={claimSettings.isAtOpenCasesLimit ? "warning" : "default"}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            {totalMyCases > 0 ? (
              <Chip
                size="small"
                icon={<PendingActionsIcon />}
                label={`${formatPersianNumber(totalMyCases)} کار`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          opacity: loading ? 0.55 : 1,
          transition: "opacity 0.2s ease",
          pointerEvents: loading ? "none" : "auto",
        }}
      >
      {error ? (
        <Alert severity="error" sx={{ mb: SECTION_SPACING, borderRadius: "12px" }}>
          {error}
        </Alert>
      ) : null}

      {claimSettings.isAtOpenCasesLimit ? (
        <Alert severity="warning" sx={{ mb: SECTION_SPACING, borderRadius: "12px", py: 0.5 }}>
          {claimSettings.limitMessage || "شما به سقف پرونده‌های فعال رسیده‌اید."}
        </Alert>
      ) : null}

      {/* 1. MY CASES */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <SectionHeader title="پرونده‌های من" subtitle="کارهای محول‌شده" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(5, 1fr)",
            },
            gap: 1.5,
          }}
        >
          {MY_CASE_ITEMS.map((item) => {
            const count = workspace.myTasks[item.key] || 0;
            return (
              <Card
                key={item.key}
                sx={{
                  ...CARD_SX,
                  cursor: "pointer",
                  borderRight: `3px solid ${item.color}`,
                  "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                }}
                onClick={() => navigate(item.path)}
              >
                <CardContent sx={{ py: 1.25, px: 1.5, "&:last-child": { pb: 1.25 } }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.title}
                  </Typography>
                  <Typography fontWeight={800} fontSize={22} color={item.color} lineHeight={1.2}>
                    {formatPersianNumber(count)}
                  </Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* 2. URGENT CASES */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <SectionHeader title="پرونده‌های فوری" subtitle="اولویت‌بندی بر اساس SLA و مدت انتظار" />
            {workspace.urgentCases.length === 0 ? (
              <CompactEmptyState message="هیچ پرونده فوری وجود ندارد." icon={TaskAltIcon} />
            ) : (
              <Stack spacing={1} divider={<Divider flexItem />}>
                {workspace.urgentCases.map((item) => {
                  const urgencyColor = getUrgencyColor(item.waitingHours, item.waitingDays);
                  return (
                    <Box
                      key={item.requestId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                        cursor: "pointer",
                        borderRadius: "10px",
                        py: 0.75,
                        px: 1,
                        borderRight: `3px solid ${urgencyColor}`,
                        "&:hover": { bgcolor: "#f5f8ff" },
                      }}
                      onClick={() => navigate(`${PATHS.projectWorkspace}/${item.requestId}`)}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Typography fontWeight={700} fontSize={13} noWrap>
                            {item.title}
                          </Typography>
                          {item.referenceId ? (
                            <Chip
                              size="small"
                              label={`#${formatPersianNumber(item.referenceId)}`}
                              variant="outlined"
                              sx={{ height: 22, fontSize: 11 }}
                            />
                          ) : null}
                        </Stack>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          flexWrap="wrap"
                          sx={{ mt: 0.25 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            SLA: {item.slaStatusLabel || "—"}
                          </Typography>
                          <Typography variant="caption" color={urgencyColor} fontWeight={600}>
                            انتظار: {getWaitingDurationLabel(item)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            آخرین فعالیت: {item.lastActivityLabel || "—"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {item.showUrgentBadge ? (
                            <Chip size="small" label="فوری" color="error" sx={{ height: 20, fontSize: 10 }} />
                          ) : null}
                          {item.showFollowUpBadge ? (
                            <Chip
                              size="small"
                              label="نیازمند پیگیری"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          ) : null}
                          {item.showOverdueBadge ? (
                            <Chip
                              size="small"
                              label="معوق"
                              color="error"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          ) : null}
                        </Stack>
                      </Box>
                      <Typography
                        fontWeight={800}
                        fontSize={16}
                        sx={{ color: urgencyColor, flexShrink: 0 }}
                      >
                        {item.stageTitle || "—"}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* 3. NEW REQUEST QUEUE */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <SectionHeader
              title="درخواست‌های جدید"
              subtitle="بدون مسئول — حداکثر ۵ مورد"
              action={
                <Button
                  size="small"
                  variant="text"
                  onClick={() => navigate(PATHS.checkNew)}
                  sx={{ fontSize: 12, fontWeight: 600 }}
                >
                  مشاهده همه درخواست‌ها
                </Button>
              }
            />
            {newRequestQueue.length === 0 ? (
              <CompactEmptyState
                message="هیچ درخواست جدیدی برای تخصیص وجود ندارد."
                icon={InboxOutlinedIcon}
              />
            ) : (
              <Stack spacing={1}>
                {newRequestQueue.map((item) => (
                  <Box
                    key={item.requestId}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      justifyContent: "space-between",
                      gap: 1,
                      py: 0.75,
                      px: 1,
                      borderRadius: "10px",
                      bgcolor: "#fafbfc",
                      border: "1px solid #eee",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} fontSize={13}>
                        {item.customerName}
                      </Typography>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">
                          {item.cityName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.capacityTitle}
                        </Typography>
                        <Typography variant="caption" color="warning.dark" fontWeight={600}>
                          {item.waitingTimeLabel}
                        </Typography>
                      </Stack>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!claimSettings.canClaimRequests || claimingId === item.requestId}
                      onClick={() => handleClaim(item.requestId)}
                      sx={{ borderRadius: "8px", minWidth: 110, flexShrink: 0 }}
                    >
                      {claimingId === item.requestId ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        "دریافت پرونده"
                      )}
                    </Button>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* 4. WORKLOAD SUMMARY */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <SectionHeader title="خلاصه بار کاری" />
            <CompactKpiRow items={WORKLOAD_KPIS} data={workload} />
          </CardContent>
        </Card>
      </Box>

      {/* 5. TAB WORKSPACE */}
      <Box sx={{ mb: SECTION_SPACING }}>
        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 40,
                mb: 0.5,
                borderBottom: "1px solid #eee",
                "& .MuiTab-root": {
                  minHeight: 40,
                  py: 0.75,
                  px: 2,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                },
              }}
            >
              <Tab label="خط لوله پرونده‌ها" />
              <Tab label="فعالیت‌های امروز" />
              <Tab label="عملکرد شخصی" />
              <Tab label="روند ۳۰ روزه" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              {!hasPipelineData ? (
                <CompactEmptyState
                  message="هنوز پرونده‌ای در این مرحله وجود ندارد."
                  icon={LeaderboardIcon}
                />
              ) : (
                <Grid container spacing={1.5}>
                  {workspace.workflowPipeline.map((stage) => {
                    const color = PIPELINE_COLORS[stage.key] || "#1976d2";
                    const pct = ((stage.count || 0) / maxPipelineCount) * 100;
                    return (
                      <Grid item xs={6} sm={4} md={3} key={stage.key}>
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: "12px",
                            bgcolor: "#fafbfc",
                            border: "1px solid #eee",
                            cursor: stage.count > 0 ? "pointer" : "default",
                            "&:hover": stage.count > 0 ? { bgcolor: "#f0f4ff" } : {},
                          }}
                          onClick={() => stage.count > 0 && navigate(PATHS.inProcess)}
                        >
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 0.75 }}
                          >
                            <Typography fontWeight={600} fontSize={12}>
                              {stage.title}
                            </Typography>
                            <Typography fontWeight={800} color={color} fontSize={18}>
                              {formatPersianNumber(stage.count)}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              bgcolor: "#e8eaf0",
                              "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 2 },
                            }}
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {workspace.todaysActivities.length === 0 ? (
                <CompactEmptyState
                  message="فعالیتی برای امروز ثبت نشده است."
                  icon={EventNoteIcon}
                />
              ) : (
                <Stack spacing={0.75} divider={<Divider flexItem />}>
                  {workspace.todaysActivities.map((activity, idx) => (
                    <Stack
                      key={`${activity.requestId}-${idx}`}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        cursor: "pointer",
                        borderRadius: "8px",
                        py: 0.5,
                        "&:hover": { bgcolor: "#f5f8ff" },
                      }}
                      onClick={() =>
                        navigate(`${PATHS.projectWorkspace}/${activity.requestId}`)
                      }
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ flexShrink: 0, minWidth: 44, fontWeight: 600 }}
                      >
                        {formatTime(activity.createOn)}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={600} fontSize={13}>
                          {activity.description || activity.updateTypeTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {activity.referenceId
                            ? `کد ${formatPersianNumber(activity.referenceId)}`
                            : `پروژه #${formatPersianNumber(activity.requestId)}`}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {!hasPerformanceData ? (
                <CompactEmptyState
                  message="هنوز داده کافی برای محاسبه عملکرد وجود ندارد."
                  icon={SpeedIcon}
                />
              ) : (
                <CompactKpiRow items={PERFORMANCE_KPIS} data={workload} />
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Suspense
                fallback={
                  <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                    <CircularProgress size={28} />
                  </Box>
                }
              >
                <GrowthConversionAnalyticsPanel
                  analytics={growthAnalytics}
                  loading={growthLoading}
                  showPeriodFilter={false}
                  height={280}
                />
              </Suspense>
            </TabPanel>
          </CardContent>
        </Card>
      </Box>

      {/* Team Performance — managers only */}
      {workspace.showTeamPerformance && workspace.teamPerformance.length > 0 ? (
        <Box sx={{ mb: SECTION_SPACING }}>
          <Card sx={CARD_SX}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <SectionHeader title="عملکرد تیم" subtitle="مدیریت" />
              <Stack spacing={1} divider={<Divider flexItem />}>
                {workspace.teamPerformance.map((row) => (
                  <Stack
                    key={row.operatorUserId}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                    sx={{ py: 0.25 }}
                  >
                    <Typography fontWeight={700} fontSize={13}>
                      {formatPersianNumber(row.rank)}. {row.operatorName}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={`باز: ${formatPersianNumber(row.openCases)}`} variant="outlined" />
                      <Chip
                        size="small"
                        label={`تکمیل: ${formatPersianNumber(row.completedCases)}`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`پاسخ: ${formatResponseTime(row.averageResponseTimeHours)}`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`معوق: ${formatPersianNumber(row.overdueCases)}`}
                        color={row.overdueCases > 0 ? "error" : "default"}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      ) : null}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SolarentOperatorPage;
